import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success("Login successful!");
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30 p-4">
      {/* Centered Logo */}
      <div className="mb-6 text-center">
        <div className="inline-block p-3 bg-white rounded-2xl shadow-lg mb-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_hub-dispatch-mgmt/artifacts/ncecubvs_rr%20logo.jpg" 
            alt="Recompose Logo" 
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
          />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-800">Recompose</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Recycling Management System</p>
      </div>

      {/* Login Form */}
      <Card className="w-full max-w-sm sm:max-w-md shadow-xl border border-gray-200" data-testid="login-card">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-xl sm:text-2xl font-bold text-center text-gray-800">
            Welcome Back
          </CardTitle>
          <CardDescription className="text-center text-sm sm:text-base">
            Enter your credentials to access your hub
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">Email Address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 sm:h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 sm:h-11"
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              className="w-full h-10 sm:h-11 bg-gradient-to-r from-[#1B5E20] to-[#6BB6A] hover:opacity-90 transition-all text-sm sm:text-base font-semibold"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <div className="text-center">
              <a 
                href="/forgot-password" 
                className="text-sm font-medium hover:underline"
                style={{ color: '#009245' }}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </a>
            </div>
            <div className="text-center text-sm pt-2 border-t">
              <span className="text-gray-600">Don't have an account? </span>
              <Link 
                to="/signup" 
                className="font-medium hover:underline"
                style={{ color: '#009245' }}
                data-testid="signup-link"
              >
                Sign Up
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
